"use client";
import React, { MouseEvent } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { faBell, faGlasses, faTrashAlt } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";
import Image from "next/image";
import Link from "next/link";

import { ButtonGroup, ThemedPage } from "@/components";
import Button from "@/components/button";
import { ButtonBar, Main, Module } from "@/components/layouts";
import PageHeading from "@/components/page-heading";
import { NotificationsQuery } from "@/gql/generated/graphql";
import { MUTATE_CLEAR_NOTIFICATIONS } from "@/gql/queries/mutate-clear-notifications";
import { MUTATE_MARK_NOTIFICATIONS_AS_READ } from "@/gql/queries/mutate-mark-notifications-as-read";
import { MUTATE_NOTIFICATION } from "@/gql/queries/mutate-notification";
import { NOTIFICATIONS } from "@/gql/queries/notifications";

const renderNotificationItem = (
  notification: NotificationsQuery["notifications"][number],
  onNotificationClick: (e: MouseEvent<HTMLAnchorElement>) => void,
) => {
  return (
    <li key={notification.id}>
      <Link
        data-id={notification.id}
        data-link={notification.link}
        href={notification.link ?? "#"}
        onClick={notification.viewed ? undefined : onNotificationClick}
      >
        {notification.text}
      </Link>{" "}
      {notification.viewed ? null : (
        <FontAwesomeIcon icon={faBell} className="notification-icon" />
      )}
      <span className="notification-date">
        {dayjs(notification.updatedAt).fromNow()}
      </span>
    </li>
  );
};

export default function NotificationsPage() {
  const { data = { notifications: [] } } = useQuery(NOTIFICATIONS, {
    fetchPolicy: "cache-and-network",
  });
  const [mutateNotification] = useMutation(MUTATE_NOTIFICATION);
  const [mutateClearNotifications] = useMutation(MUTATE_CLEAR_NOTIFICATIONS, {
    update(cache) {
      cache.writeQuery({
        query: NOTIFICATIONS,
        data: { notifications: [] },
      });
    },
    refetchQueries: ["NotificationsCount"],
  });
  const [mutateMarkNotificationsAsRead] = useMutation(
    MUTATE_MARK_NOTIFICATIONS_AS_READ,
  );

  const onNotificationClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!e.currentTarget.dataset.id) return;

    mutateNotification({
      variables: { id: e.currentTarget.dataset.id, viewed: true },
    });
  };

  const onViewedAllNotificationsClick = () => {
    mutateMarkNotificationsAsRead();
  };

  const onClearAllNotificationsClick = () => {
    mutateClearNotifications();
  };

  return (
    <ThemedPage>
      <Main>
        <PageHeading>My Notifications</PageHeading>
        <ButtonBar>
          <ButtonGroup>
            <Button
              icon={faGlasses}
              onClick={onViewedAllNotificationsClick}
            >
              Mark all as read
            </Button>
            <Button
              icon={faTrashAlt}
              type="negative"
              onClick={onClearAllNotificationsClick}
            >
              Clear all
            </Button>
          </ButtonGroup>
        </ButtonBar>
        <Module>
          <p>
            Notifications are automatically cleared one hour after being viewed,
            or one week after being sent.
          </p>
        </Module>
        {data.notifications.length === 0 ? (
          <Module>
            <div className="split-layout">
              <div className="split-layout--small">
                <Image
                  src="/images/blue-monster.jpg"
                  alt=""
                  style={{ width: "100%" }}
                  width={100}
                  height={100}
                />
              </div>
              <div className="split-layout--large">
                <p>No notifications. You&apos;re all caught up! </p>
              </div>
            </div>
          </Module>
        ) : null}
        <Module>
          <ol className="notifications-list">
            {data.notifications.map((notification) =>
              renderNotificationItem(notification, onNotificationClick),
            )}
          </ol>
        </Module>
      </Main>
    </ThemedPage>
  );
}
